export interface SfpBoxItem {
  id: string;
  name: string;
  sourceForm?: string | null;
  roundedAmountStr: string;
  roundedPercentDvStr?: string | null;
  group: 'b2_nutrient' | 'b3_dietary_ingredient';
  footnoteSymbol?: string | null;
}

export interface SfpBoxData {
  title: string;
  servingSize: string;
  servingsPerContainer: string;
  items: SfpBoxItem[];
  hasB3Ingredients: boolean;
  hasPercentDv: boolean;
  calorieBaselineFootnote: string | null;
  dvNotEstablishedFootnote: string | null;
  otherIngredients: string[];
  allergens: string[];
  warnings: string[];
}

export function SupplementFactsBox({ data }: { data: SfpBoxData }) {
  const b2Items = data.items.filter((i) => i.group === 'b2_nutrient');
  const b3Items = data.items.filter((i) => i.group === 'b3_dietary_ingredient');

  return (
    <div className="max-w-md font-sans text-slate-950">
      {/* Outer FDA boxed panel */}
      <div className="border-[2.5px] border-black bg-white p-3 shadow-sm">
        {/* Title */}
        <h2 className="text-2xl font-black tracking-tight text-black leading-none">{data.title}</h2>

        {/* Serving info */}
        <div className="mt-1.5 text-xs text-black">
          <div className="flex justify-between font-normal">
            <span>Serving Size {data.servingSize}</span>
          </div>
          <div className="flex justify-between font-normal">
            <span>Servings Per Container {data.servingsPerContainer}</span>
          </div>
        </div>

        {/* Heavy line under serving info */}
        <div className="my-1.5 h-[6px] bg-black" />

        {/* Column headers */}
        <div className="flex items-baseline justify-between text-[11px] font-bold text-black pb-0.5">
          <span>Amount Per Serving</span>
          <span>% Daily Value*</span>
        </div>

        {/* Medium bar under header */}
        <div className="h-[2.5px] bg-black" />

        {/* (b)(2) Nutrients */}
        <div className="divide-y divide-black/40 text-xs">
          {b2Items.map((item) => (
            <div key={item.id} className="flex justify-between py-1 items-baseline">
              <span className="font-semibold text-black">
                {item.name}{' '}
                {item.sourceForm && (
                  <span className="font-normal text-slate-700 text-[11px]">{item.sourceForm}</span>
                )}{' '}
                <span className="font-normal text-black">{item.roundedAmountStr}</span>
              </span>
              <span className="font-bold text-black font-mono">{item.roundedPercentDvStr ?? '-'}</span>
            </div>
          ))}
        </div>

        {/* Heavy line before (b)(3) ingredients (if present) */}
        {b3Items.length > 0 && (
          <>
            <div className="h-[4px] bg-black my-0.5" />
            <div className="divide-y divide-black/40 text-xs">
              {b3Items.map((item) => (
                <div key={item.id} className="flex justify-between py-1 items-baseline">
                  <span className="font-normal text-black">
                    {item.name}{' '}
                    {item.sourceForm && <span className="text-slate-700 text-[11px]">{item.sourceForm}</span>}{' '}
                    <span>{item.roundedAmountStr}</span>
                  </span>
                  <span className="font-bold text-black font-mono">†</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Heavy line before footnotes */}
        <div className="h-[4px] bg-black mt-1 mb-1.5" />

        {/* Footnotes */}
        <div className="space-y-0.5 text-[10px] leading-tight text-black">
          {data.calorieBaselineFootnote && <p>* {data.calorieBaselineFootnote}</p>}
          {data.dvNotEstablishedFootnote && <p>{data.dvNotEstablishedFootnote}</p>}
        </div>
      </div>

      {/* Other Ingredients (21 CFR 101.4(g) - strictly outside SFP) */}
      {data.otherIngredients.length > 0 && (
        <div className="mt-2.5 text-xs text-slate-800 leading-normal">
          <span className="font-bold">Other ingredients:</span> {data.otherIngredients.join(', ')}.
        </div>
      )}

      {/* Allergens statement */}
      {data.allergens.length > 0 && (
        <div className="mt-1.5 text-xs font-semibold text-slate-900">
          <span>Contains:</span> {data.allergens.join(', ')}.
        </div>
      )}

      {/* Mandatory Warnings (e.g. Iron) */}
      {data.warnings.length > 0 && (
        <div className="mt-2.5 border border-red-700 bg-red-50 p-2 text-xs text-red-900">
          {data.warnings.map((w, idx) => (
            <p key={idx} className="font-medium">
              {w}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
